function ExternalLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      className="font-medium text-primary underline underline-offset-2 hover:no-underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

import { SEO } from '@/components/SEO';

export default function PrivacyPolicyPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <SEO
        title="Política de privacidad — LÍDER IA"
        description="Política de privacidad y términos legales de LÍDER IA, operada por VIVETORI S.A.S."
        path="/privacy-policy"
      />
      <header className="mb-10">
        <h1 className="mb-3 text-4xl font-bold tracking-tight text-foreground">Política de privacidad y términos legales</h1>
        <p className="text-muted-foreground">
          Documento que incluye los Términos y Condiciones de uso de LIDER IA y la Política de Protección de Datos
          Personales de VIVETORI S.A.S.
        </p>
      </header>

      <article className="prose prose-sm max-w-none text-foreground prose-headings:scroll-mt-24 prose-headings:font-semibold prose-p:text-foreground/90 prose-li:text-foreground/90 prose-strong:text-foreground">
        <h2 className="border-b border-border pb-2 text-2xl">Términos y Condiciones LIDER IA</h2>

        <h3>1. Información general y aceptación</h3>
        <h4>1.1. Identificación del responsable</h4>
        <p>
          La presente plataforma digital, disponible en{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, es operada por la
          sociedad <strong>VIVETORI S.A.S.</strong>, identificada con NIT 901560075, domiciliada en Colombia, y titular de
          la marca comercial <strong>LIDER IA</strong>.
        </p>
        <p>Para efectos de notificaciones legales o contacto formal, se ha habilitado el siguiente canal:</p>
        <p>
          <strong>Correo electrónico:</strong>{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
        </p>

        <h4>1.2. Aceptación de los Términos y Condiciones</h4>
        <p>
          El presente documento establece los términos legales y condiciones de uso que regulan el acceso, navegación y
          utilización de la plataforma LIDER IA, así como la contratación de sus servicios, suscripciones o rutas de
          certificación.
        </p>
        <p>
          Al registrarse, acceder, utilizar o realizar cualquier acción dentro de la plataforma, el usuario acepta
          expresamente estos Términos y Condiciones. En caso de no estar de acuerdo con su contenido, deberá abstenerse
          de utilizar los servicios ofrecidos.
        </p>
        <p>
          Estos Términos y Condiciones constituyen un acuerdo vinculante entre el usuario y VIVETORI S.A.S. – LIDER IA,
          y son aplicables a todas las personas naturales o jurídicas que accedan a la plataforma, independientemente del
          país desde el cual se conecten.
        </p>

        <h3>2. Definiciones</h3>
        <p>Para facilitar la comprensión de estos Términos y Condiciones, se establecen las siguientes definiciones:</p>
        <dl className="not-prose space-y-4 text-sm">
          <div>
            <dt className="font-semibold text-foreground">LIDER IA</dt>
            <dd className="mt-1 text-muted-foreground">
              Marca comercial de propiedad de VIVETORI S.A.S., bajo la cual se ofrece la plataforma tecnológica de
              formación, certificación y acompañamiento en inteligencia artificial para empresas y profesionales.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">La Plataforma</dt>
            <dd className="mt-1 text-muted-foreground">
              Sitio web y sistema digital accesible desde{' '}
              <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, en el cual los
              usuarios acceden a contenidos, herramientas, rutas y servicios de LIDER IA.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Usuario</dt>
            <dd className="mt-1 text-muted-foreground">
              Persona natural o jurídica que se registra en la plataforma y hace uso de sus servicios, ya sea como
              suscriptor, participante de una ruta de certificación o visitante.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Cuenta de Usuario</dt>
            <dd className="mt-1 text-muted-foreground">
              Registro individual asociado a una dirección de correo electrónico, desde el cual se accede a los
              contenidos y funcionalidades contratadas dentro de la plataforma.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Contenidos</dt>
            <dd className="mt-1 text-muted-foreground">
              Todo material digital disponible en la plataforma, incluyendo cápsulas de aprendizaje, guías, recursos
              descargables, instructivos, plantillas, simuladores, y demás materiales formativos o informativos.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Rutas de Certificación</dt>
            <dd className="mt-1 text-muted-foreground">
              Rutas de formación ofrecidas por LIDER IA con pago único, que otorgan al usuario una certificación tras
              completar satisfactoriamente los módulos, ejercicios y evaluaciones requeridas.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Suscripción</dt>
            <dd className="mt-1 text-muted-foreground">
              Modelo de acceso mensual o anual que permite al usuario utilizar una serie de funcionalidades y contenidos
              en la plataforma mientras se mantenga activo el plan contratado.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Datos Personales</dt>
            <dd className="mt-1 text-muted-foreground">
              Toda información que permita identificar o hacer identificable a una persona natural, recolectada o
              tratada dentro del uso de la plataforma.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Términos y Condiciones</dt>
            <dd className="mt-1 text-muted-foreground">
              Este documento, que regula las obligaciones, derechos, limitaciones y alcances del uso de la plataforma y
              sus servicios.
            </dd>
          </div>
        </dl>

        <h3>3. Objeto del contrato</h3>
        <p>
          El presente contrato tiene por objeto regular el acceso, uso y condiciones bajo las cuales los usuarios pueden
          interactuar con la plataforma LIDER IA, operada por VIVETORI S.A.S., así como la adquisición de sus servicios
          de formación, acompañamiento y certificación en inteligencia artificial.
        </p>
        <p>Mediante la aceptación de estos Términos y Condiciones, el usuario podrá:</p>
        <ul>
          <li>Registrarse y crear una cuenta personal o corporativa.</li>
          <li>
            Acceder a los contenidos educativos ofrecidos por LIDER IA, según el plan de suscripción o ruta de
            certificación adquirido.
          </li>
          <li>
            Hacer uso de herramientas, guías y simuladores disponibles en la plataforma para el diseño, desarrollo o
            implementación de soluciones de inteligencia artificial.
          </li>
          <li>Participar en procesos de formación certificados ofrecidos por LIDER IA.</li>
          <li>
            Contactar con el equipo de soporte o consultoría, de acuerdo con las modalidades incluidas en su plan o
            producto contratado.
          </li>
        </ul>
        <p>
          Este contrato no otorga al usuario ningún derecho sobre los contenidos, marcas o funcionalidades de la
          plataforma más allá del acceso limitado previsto en la licencia de uso definida en el capítulo 7.
        </p>

        <h3>4. Registro y acceso a la plataforma</h3>
        <h4>4.1. Creación de cuentas y credenciales</h4>
        <p>
          Para acceder a los servicios de LIDER IA, el usuario deberá registrarse en la plataforma a través del sitio{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, proporcionando
          información veraz, completa y actualizada.
        </p>
        <p>
          Cada cuenta es personal e intransferible. El usuario es responsable de custodiar sus credenciales (correo y
          contraseña) y se compromete a no compartirlas con terceros.
        </p>
        <p>
          Cualquier actividad realizada desde su cuenta se entenderá realizada por el titular, salvo que notifique de
          inmediato a LIDER IA sobre uso no autorizado o sospechas de vulneración.
        </p>
        <p>
          LIDER IA se reserva el derecho de verificar la identidad de los usuarios y, en caso de inconsistencias o uso
          indebido, suspender o cancelar cuentas sin previo aviso.
        </p>

        <h4>4.2. Requisitos de edad y capacidad</h4>
        <p>
          El uso de la plataforma está dirigido a personas mayores de edad (18 años o más). No obstante, menores de edad
          podrán acceder a los contenidos y rutas siempre que estén inscritos o supervisados por una persona adulta
          responsable, como parte de una ruta corporativa, institucional o educativa autorizado.
        </p>
        <p>
          Es responsabilidad del adulto garantizar el adecuado uso y cumplimiento de estos Términos y Condiciones por
          parte del menor bajo su cargo.
        </p>

        <h3>5. Planes de suscripción y rutas de certificación</h3>
        <h4>5.1. Tipos de planes</h4>
        <p>LIDER IA ofrece dos modelos principales de acceso a sus servicios:</p>
        <ul>
          <li>
            <strong>Planes de suscripción:</strong> Acceso continuo a contenidos y funcionalidades de la plataforma
            mediante pago mensual o anual. Los precios actualizados están disponibles en el sitio oficial{' '}
            <ExternalLink href="https://www.soylideria.com">www.soylideria.com</ExternalLink>.
          </li>
          <li>
            <strong>Rutas de certificación:</strong> Acceso a rutas de formación específicas mediante un pago único.
            Incluyen módulos estructurados, evaluaciones y entrega de certificado al completar satisfactoriamente el
            proceso.
          </li>
        </ul>
        <p>
          El contenido disponible, la duración y los beneficios incluidos pueden variar según el tipo de plan
          seleccionado. LIDER IA se reserva el derecho de actualizar o modificar la oferta de servicios, garantizando que
          los usuarios activos mantengan acceso a lo contratado.
        </p>

        <h4>5.2. Formas y condiciones de pago</h4>
        <p>Los pagos pueden realizarse mediante:</p>
        <ul>
          <li>Tarjeta de crédito (internacional).</li>
          <li>Transferencia bancaria, disponible exclusivamente los primeros 5 días de cada mes.</li>
        </ul>
        <p>
          Todos los cobros se realizan en dólares estadounidenses (USD). El usuario es responsable de cualquier comisión,
          cargo bancario o conversión aplicable según su entidad financiera o país.
        </p>

        <h4>5.3. Facturación y tributos</h4>
        <p>
          Salvo que se indique lo contrario, todos los precios incluyen los impuestos aplicables según la normativa
          vigente. Para efectos contables, los usuarios pueden solicitar factura o comprobante de pago escribiendo a{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>{' '}
          con los datos correspondientes.
        </p>

        <h3>6. Cancelación de suscripciones y política de reembolsos</h3>
        <h4>6.1. Cancelación de suscripciones</h4>
        <p>
          El usuario podrá cancelar en cualquier momento su suscripción mensual directamente desde la plataforma,
          accediendo a la sección de gestión de suscripción en{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>.
        </p>
        <p>
          La cancelación detiene la renovación automática para el siguiente periodo, pero no implica reembolso del periodo
          ya pagado. El acceso a los contenidos continuará activo hasta el final del ciclo de facturación vigente.
        </p>
        <p>
          En el caso de suscripciones anuales, la cancelación también puede realizarse en cualquier momento, pero el
          acceso y vigencia se mantendrán hasta la fecha de vencimiento del plan anual contratado.
        </p>

        <h4>6.2. Reembolsos</h4>
        <p>
          LIDER IA no ofrece reembolsos automáticos. Sin embargo, situaciones particulares podrán ser evaluadas a
          solicitud del usuario, quien deberá enviar su requerimiento al correo{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          , indicando:
        </p>
        <ul>
          <li>Motivo de la solicitud.</li>
          <li>Correo asociado a la cuenta.</li>
          <li>Fecha de pago y comprobante (si aplica).</li>
        </ul>
        <p>
          Cada caso será analizado individualmente, y LIDER IA se reserva el derecho de aceptar o rechazar la solicitud
          de reembolso según sus políticas internas.
        </p>

        <h3>7. Licencia de uso y propiedad intelectual</h3>
        <h4>7.1. Contenidos protegidos</h4>
        <p>
          Todos los contenidos desarrollados por LIDER IA —incluyendo cápsulas de aprendizaje, guías, simuladores,
          videos, textos, gráficas, metodologías y estructuras de las rutas— son propiedad intelectual de VIVETORI
          S.A.S. y están protegidos por las normas nacionales e internacionales sobre derechos de autor.
        </p>
        <p>
          Mediante la suscripción o participación en una ruta, el usuario obtiene una licencia de uso limitada, no
          exclusiva, intransferible y revocable para acceder y utilizar dichos contenidos únicamente con fines educativos
          y profesionales, conforme a lo establecido en estos Términos y Condiciones.
        </p>
        <p>
          Queda estrictamente prohibida la reproducción, distribución, transformación, sublicenciamiento o venta de los
          contenidos sin autorización previa y por escrito de LIDER IA.
        </p>

        <h4>7.2. Contenidos abiertos</h4>
        <p>
          Algunas plantillas, recursos descargables y ejemplos prácticos puestos a disposición dentro de la plataforma
          pueden estar catalogados como contenido abierto o reutilizable. En dichos casos, se indicará expresamente que
          pueden ser modificados o adaptados por el usuario para su uso interno.
        </p>
        <p>
          Esto no otorga ningún derecho sobre la marca, imagen o estructura general de la plataforma ni exime del
          cumplimiento del presente contrato.
        </p>

        <h4>7.3. Titularidad de proyectos desarrollados</h4>
        <p>
          Los proyectos, documentos o ejercicios desarrollados por el usuario en el marco de su experiencia formativa o
          de consultoría en la plataforma seguirán siendo propiedad del usuario. LIDER IA no reclama derechos sobre
          dichas creaciones.
        </p>

        <h4>7.4. Uso de proyectos como caso de éxito</h4>
        <p>
          El usuario autoriza a LIDER IA a referenciar su proyecto, experiencia o resultados como caso de éxito, en
          contextos académicos, comerciales o de divulgación, sin requerir aprobación previa. Esta autorización se otorga
          por el solo hecho de utilizar la plataforma y sus servicios, y podrá ser revocada en cualquier momento mediante
          solicitud escrita a{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          .
        </p>

        <h3>8. Confidencialidad</h3>
        <p>
          Todos los contenidos, procesos, estructuras metodológicas y herramientas ofrecidas por LIDER IA dentro de su
          plataforma se consideran información confidencial y de uso restringido.
        </p>
        <p>
          Asimismo, cualquier información que el usuario comparta dentro de la plataforma —incluyendo, pero sin
          limitarse a: ideas de proyectos, análisis internos, estrategias, documentos cargados, respuestas escritas o
          formularios completados— será tratada bajo estándares de confidencialidad y reserva, y no será divulgada
          públicamente sin autorización, salvo que:
        </p>
        <ul>
          <li>Sea información de conocimiento público;</li>
          <li>Haya sido obtenida legítimamente por terceros;</li>
          <li>Sea requerida por autoridad competente mediante orden legal.</li>
        </ul>

        <h4>8.1. Acuerdo de confidencialidad incorporado</h4>
        <p>
          Estos Términos y Condiciones incorporan un acuerdo de confidencialidad (NDA) de mutuo cumplimiento entre el
          usuario y LIDER IA. Por tanto, ambas partes se obligan a:
        </p>
        <ul>
          <li>
            No divulgar ni permitir el acceso de terceros a la información sensible compartida a través de la
            plataforma.
          </li>
          <li>
            Usar dicha información únicamente para los fines autorizados dentro del marco de la ruta o servicio
            contratado.
          </li>
        </ul>
        <p>Cualquier incumplimiento dará lugar a las acciones legales correspondientes.</p>

        <h3>9. Protección de datos personales</h3>
        <p>
          LIDER IA y VIVETORI S.A.S. se comprometen al cumplimiento integral de la Ley 1581 de 2012 y sus decretos
          reglamentarios sobre protección de datos personales en Colombia, así como del Reglamento General de Protección
          de Datos (GDPR) de la Unión Europea, cuando aplique.
        </p>

        <h4>9.1. Datos recolectados y finalidades</h4>
        <p>Durante el uso de la plataforma, LIDER IA podrá recolectar y tratar los siguientes tipos de datos personales:</p>
        <ul>
          <li>
            <strong>Datos de identificación:</strong> nombre, correo electrónico, documento de identidad, empresa u
            organización.
          </li>
          <li>
            <strong>Información de uso:</strong> actividad dentro de la plataforma, progreso, interacción con contenidos,
            resultados de ejercicios.
          </li>
          <li>
            <strong>Datos de facturación y pago:</strong> únicamente cuando el usuario adquiera productos o servicios
            pagos.
          </li>
        </ul>
        <p>Estos datos se recolectan con las siguientes finalidades:</p>
        <ul>
          <li>Gestionar el acceso a los servicios contratados.</li>
          <li>Emitir certificados y validar progresos.</li>
          <li>Prestar soporte técnico y operativo.</li>
          <li>Realizar análisis internos para la mejora continua.</li>
          <li>
            Enviar comunicaciones comerciales, académicas o de actualización (con opción de desuscribirse).
          </li>
        </ul>

        <h4>9.2. Responsable y encargado del tratamiento</h4>
        <ul>
          <li>
            <strong>Responsable del tratamiento:</strong> VIVETORI S.A.S. – NIT 901560075.
          </li>
          <li>
            <strong>Encargado del tratamiento:</strong> El equipo operativo y tecnológico de LIDER IA, bajo estricta
            política de acceso restringido y controlado.
          </li>
        </ul>

        <h4>9.3. Derechos del titular de los datos</h4>
        <p>
          El usuario, en calidad de titular, podrá ejercer en cualquier momento los derechos de acceso, corrección,
          actualización, supresión y revocatoria de autorización (derechos ARCO o habeas data), mediante solicitud
          escrita al correo{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          .
        </p>
        <p>
          Toda solicitud será atendida en un plazo máximo de 15 días hábiles, conforme a la regulación aplicable.
        </p>

        <h4>9.4. Conservación de la información</h4>
        <p>
          Los datos personales recolectados serán conservados durante el tiempo que sea necesario para cumplir con la
          finalidad para la cual fueron recopilados, o mientras exista una relación activa con el usuario. Finalizado
          este periodo, los datos serán eliminados o anonimizados, salvo que exista obligación legal de conservación por
          más tiempo.
        </p>

        <h3>10. Uso aceptable de la plataforma</h3>
        <p>
          El acceso a la plataforma LIDER IA debe realizarse de manera responsable, ética y conforme a la legislación
          vigente. El usuario se compromete a no utilizar los servicios para fines ilegales, fraudulentos, ofensivos o
          que afecten negativamente a otros usuarios o a la operación de la plataforma.
        </p>

        <h4>10.1. Actividades prohibidas</h4>
        <p>Está estrictamente prohibido:</p>
        <ul>
          <li>
            Copiar, distribuir, grabar, modificar o revender los contenidos protegidos por derechos de autor sin
            autorización expresa de LIDER IA.
          </li>
          <li>Compartir el acceso a la cuenta personal con terceros o utilizar credenciales ajenas.</li>
          <li>
            Ejecutar ingeniería inversa, descompilación o cualquier técnica destinada a descubrir el código fuente o
            estructura interna de la plataforma.
          </li>
          <li>
            Utilizar la plataforma para cargar, compartir o desarrollar contenidos que inciten al odio, discriminación,
            violencia, infracción de derechos de autor o que violen la ley.
          </li>
          <li>Acceder sin autorización a cuentas, datos, módulos o recursos no contratados o restringidos.</li>
          <li>
            Utilizar automatizaciones, scripts o herramientas externas que afecten el funcionamiento normal del sistema.
          </li>
        </ul>

        <h4>10.2. Suspensión o terminación de cuentas</h4>
        <p>
          LIDER IA se reserva el derecho de suspender temporal o definitivamente la cuenta de cualquier usuario que:
        </p>
        <ul>
          <li>Incurra en conductas prohibidas según lo establecido en este documento.</li>
          <li>
            Utilice indebidamente los contenidos, afecte la experiencia de otros usuarios o ponga en riesgo la seguridad
            de la plataforma.
          </li>
          <li>Robar o publicar sin autorización los contenidos internos o confidenciales de las rutas.</li>
        </ul>
        <p>
          La suspensión podrá realizarse sin previo aviso y no dará lugar a reembolsos. En casos graves, LIDER IA se
          reserva el derecho de emprender acciones legales.
        </p>

        <h3>11. Soporte y niveles de servicio</h3>
        <p>
          LIDER IA pone a disposición de sus usuarios canales de atención y soporte para resolver inquietudes técnicas,
          operativas o administrativas relacionadas con el uso de la plataforma.
        </p>

        <h4>11.1. Horarios y canales de atención</h4>
        <p>El soporte se brinda de lunes a viernes, en horario de oficina:</p>
        <ul>
          <li>
            <strong>Mañana:</strong> 8:00 a.m. – 12:00 p.m.
          </li>
          <li>
            <strong>Tarde:</strong> 2:00 p.m. – 6:00 p.m.
          </li>
        </ul>
        <p>Los usuarios pueden contactar al equipo de soporte a través de:</p>
        <ul>
          <li>
            <strong>Correo electrónico:</strong>{' '}
            <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
              hello@soylideria.com
            </a>
          </li>
          <li>
            <strong>Sección de soporte</strong> disponible dentro de la plataforma:{' '}
            <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>
          </li>
        </ul>
        <p>
          LIDER IA procura dar respuesta a todas las solicitudes en un plazo máximo de 2 días hábiles, sin que ello
          constituya una obligación de respuesta inmediata.
        </p>

        <h4>11.2. Mantenimiento programado e indisponibilidad</h4>
        <p>
          La plataforma podrá estar sujeta a mantenimientos técnicos programados, durante los cuales los servicios
          podrían estar parcial o totalmente indisponibles. En lo posible, estos mantenimientos serán informados
          previamente a los usuarios mediante correo electrónico o notificación dentro de la plataforma.
        </p>
        <p>
          En casos de fallas técnicas no programadas, el equipo técnico actuará con diligencia para restablecer el
          servicio en el menor tiempo posible. No obstante, LIDER IA no será responsable por interrupciones ocasionales
          derivadas de causas ajenas a su control.
        </p>

        <h3>12. Limitación de responsabilidad</h3>
        <p>
          LIDER IA ofrece sus servicios y contenidos &quot;tal como están&quot; y &quot;según disponibilidad&quot;, sin
          garantizar que estén libres de errores, interrupciones o resultados específicos. Aunque se realizan esfuerzos
          razonables para asegurar la calidad, actualidad y disponibilidad de la plataforma, el usuario acepta
          expresamente que su uso se realiza bajo su propio riesgo.
        </p>

        <h4>12.1. Alcance de la responsabilidad</h4>
        <p>
          En ningún caso LIDER IA (VIVETORI S.A.S.), sus representantes, empleados o aliados serán responsables por:
        </p>
        <ul>
          <li>
            Pérdidas de ingresos, beneficios, datos, oportunidades comerciales u otros daños indirectos, incidentales o
            consecuentes.
          </li>
          <li>
            Resultados derivados del uso, interpretación o aplicación de los contenidos formativos dentro de contextos
            empresariales o personales.
          </li>
          <li>
            Fallos de conectividad, incompatibilidades técnicas, o errores derivados del dispositivo, navegador o red del
            usuario.
          </li>
        </ul>

        <h4>12.2. Límite máximo de responsabilidad</h4>
        <p>
          La responsabilidad total de LIDER IA frente al usuario, por cualquier causa, se limitará en todo caso al monto
          efectivamente pagado por el usuario en los últimos 12 meses anteriores al evento que dio origen a la
          reclamación.
        </p>
        <p>
          Este límite aplicará incluso si se determina la existencia de falla o incumplimiento, y será la única
          compensación posible frente a cualquier reclamación.
        </p>

        <h3>13. Modificaciones de los Términos y Condiciones</h3>
        <p>
          LIDER IA se reserva el derecho de modificar, actualizar o complementar en cualquier momento y sin previo aviso
          estos Términos y Condiciones, con el fin de ajustarlos a cambios legales, operativos o de funcionamiento de la
          plataforma.
        </p>
        <p>
          Cualquier modificación entrará en vigor desde el momento en que sea publicada en la plataforma, y se entenderá
          aceptada por el usuario con la continuidad en el uso del servicio.
        </p>
        <p>Cuando los cambios sean sustanciales o afecten directamente los derechos adquiridos del usuario, LIDER IA podrá notificarlo mediante:</p>
        <ul>
          <li>Correo electrónico registrado.</li>
          <li>Mensaje destacado dentro de la plataforma.</li>
          <li>Actualización visible en el sitio web oficial.</li>
        </ul>
        <p>
          Se recomienda al usuario revisar periódicamente este documento para mantenerse informado sobre las condiciones
          aplicables a su uso de la plataforma.
        </p>

        <h3>14. Ley aplicable y jurisdicción</h3>
        <p>
          El presente acuerdo se rige por las leyes de la República de Colombia, sin perjuicio de las disposiciones
          legales imperativas que resulten aplicables en la jurisdicción del usuario.
        </p>
        <p>
          Para todos los efectos legales derivados del uso de la plataforma LIDER IA y la relación contractual con
          VIVETORI S.A.S., las partes se someten a la jurisdicción de los jueces de la ciudad de Barranquilla, Colombia,
          renunciando expresamente a cualquier otro fuero que pudiera corresponderles por razón de su domicilio presente
          o futuro.
        </p>
        <p>
          Sin embargo, LIDER IA podrá, a su criterio, acudir previamente a mecanismos alternativos de resolución de
          conflictos como la conciliación ante centros autorizados por la Cámara de Comercio de Barranquilla.
        </p>

        <h3>15. Comunicaciones comerciales y uso del correo electrónico</h3>
        <p>
          Al registrarse en la plataforma LIDER IA, el usuario autoriza a VIVETORI S.A.S. a enviarle comunicaciones
          electrónicas relacionadas con:
        </p>
        <ul>
          <li>Actualizaciones de la plataforma y sus servicios.</li>
          <li>Recordatorios de acceso, renovaciones o vencimientos.</li>
          <li>Contenidos educativos relevantes.</li>
          <li>Invitaciones a eventos, webinars, rutas y novedades.</li>
          <li>
            Ofertas promocionales o comerciales asociadas a la temática de inteligencia artificial, formación o
            innovación.
          </li>
        </ul>
        <p>
          Estas comunicaciones se enviarán al correo electrónico registrado por el usuario. En todo momento, el usuario
          podrá ejercer su derecho a no recibir comunicaciones comerciales adicionales, utilizando el enlace de
          desuscripción incluido en cada mensaje o escribiendo a{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          .
        </p>
        <p>
          LIDER IA se compromete a no compartir ni comercializar los datos de contacto del usuario con terceros ajenos a
          la operación de sus servicios, salvo obligación legal o autorización expresa.
        </p>

        <h3>16. Contacto y notificaciones oficiales</h3>
        <p>
          Para cualquier solicitud, notificación legal, inquietud sobre estos Términos y Condiciones o ejercicio de
          derechos relacionados con el uso de la plataforma, los usuarios podrán comunicarse directamente con el equipo de
          LIDER IA a través de los siguientes medios oficiales:
        </p>
        <ul>
          <li>
            <strong>Correo electrónico:</strong>{' '}
            <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
              hello@soylideria.com
            </a>
          </li>
          <li>
            <strong>Sitio web:</strong>{' '}
            <ExternalLink href="https://www.soylideria.com">https://www.soylideria.com</ExternalLink>
          </li>
          <li>
            <strong>Plataforma de uso:</strong>{' '}
            <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>
          </li>
        </ul>
        <p>
          Las notificaciones se considerarán válidas cuando sean enviadas desde o hacia estos canales. Cualquier cambio
          en los datos de contacto será informado oportunamente a través de la plataforma.
        </p>

        <hr className="my-12 border-border" />

        <h2 className="border-b border-border pb-2 text-2xl">Política de datos personales</h2>

        <h3>1. Introducción y alcance</h3>
        <p>
          La presente Política de Protección de Datos Personales establece los lineamientos bajo los cuales{' '}
          <strong>VIVETORI S.A.S.</strong> (en adelante &quot;LIDER IA&quot;), identificada con NIT 901560075 y
          responsable de la operación de la plataforma{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, recolecta, utiliza,
          almacena, transmite y protege los datos personales de sus usuarios.
        </p>
        <p>
          Esta política aplica a todos los usuarios —personas naturales o jurídicas— que acceden o hacen uso de los
          servicios prestados por LIDER IA, ya sea a través de suscripciones mensuales o anuales, rutas de
          certificación con pago único, contenidos abiertos o acompañamiento personalizado.
        </p>
        <p>LIDER IA se compromete a garantizar la privacidad, confidencialidad y protección de los datos personales de conformidad con:</p>
        <ul>
          <li>La Ley 1581 de 2012 de Colombia y sus decretos reglamentarios.</li>
          <li>
            Las disposiciones del Reglamento General de Protección de Datos (GDPR) de la Unión Europea, en los casos en
            que sea aplicable.
          </li>
          <li>Principios de responsabilidad proactiva, minimización de datos y finalidad legítima.</li>
        </ul>
        <p>
          El uso de la plataforma implica la aceptación expresa de esta política por parte del usuario. En caso de no
          estar de acuerdo, el usuario debe abstenerse de utilizar la plataforma o ejercer sus derechos a través de los
          canales dispuestos.
        </p>

        <h3>2. Datos que recolectamos</h3>
        <p>
          LIDER IA recolecta y trata diferentes tipos de datos personales con el fin de ofrecer sus servicios de manera
          segura, personalizada y eficiente. Estos datos pueden ser proporcionados directamente por el usuario o
          recolectados automáticamente durante el uso de la plataforma.
        </p>

        <h4>2.1. Información personal proporcionada directamente</h4>
        <p>Durante el proceso de registro, compra o participación en rutas, LIDER IA podrá solicitar y almacenar:</p>
        <ul>
          <li>Nombres y apellidos.</li>
          <li>Dirección de correo electrónico.</li>
          <li>Tipo y número de documento de identificación.</li>
          <li>Empresa, organización o institución de afiliación (si aplica).</li>
          <li>Número telefónico de contacto.</li>
          <li>Información académica o profesional suministrada por el usuario.</li>
          <li>Credenciales de acceso (usuario y contraseña).</li>
        </ul>

        <h4>2.2. Información de uso y navegación</h4>
        <p>
          La plataforma puede recolectar automáticamente datos relacionados con el uso y comportamiento del usuario, tales
          como:
        </p>
        <ul>
          <li>Dirección IP, tipo de navegador y dispositivo.</li>
          <li>Zonas geográficas desde las que accede.</li>
          <li>
            Actividad en la plataforma: rutas vistas, módulos completados, evaluaciones, respuestas y retroalimentaciones.
          </li>
          <li>Fechas y horas de acceso.</li>
          <li>Preferencias o interacciones dentro del sistema.</li>
        </ul>
        <p>
          Estos datos se usan para garantizar la funcionalidad, hacer mejoras y generar reportes internos de calidad del
          servicio.
        </p>

        <h4>2.3. Información de facturación y pago</h4>
        <p>Cuando el usuario contrata servicios pagos (suscripción o certificación), también podrán ser recolectados:</p>
        <ul>
          <li>Información de métodos de pago (como tarjeta de crédito) a través de pasarelas seguras.</li>
          <li>Comprobantes de transferencias bancarias.</li>
          <li>Datos de facturación requeridos para efectos contables y tributarios.</li>
        </ul>
        <p>
          LIDER IA no almacena directamente los datos de tarjetas o medios financieros: estos son procesados por
          proveedores externos autorizados bajo estándares de seguridad PCI-DSS.
        </p>

        <h3>3. Finalidades del tratamiento</h3>
        <p>
          LIDER IA tratará los datos personales recolectados exclusivamente para las siguientes finalidades legítimas y
          explícitas:
        </p>

        <h4>3.1. Finalidades contractuales y operativas</h4>
        <ul>
          <li>Gestionar el proceso de registro y autenticación en la plataforma.</li>
          <li>Permitir el acceso a contenidos, funcionalidades y recursos digitales según el plan adquirido.</li>
          <li>Gestionar suscripciones, renovaciones, pagos y facturación.</li>
          <li>Emitir certificados de participación o finalización de rutas.</li>
          <li>Facilitar el contacto entre el usuario y el equipo de soporte, tutores o consultores asignados.</li>
        </ul>

        <h4>3.2. Finalidades académicas y de mejora continua</h4>
        <ul>
          <li>Realizar seguimiento del avance del usuario y generar métricas de uso.</li>
          <li>Diseñar rutas personalizadas de aprendizaje basadas en el comportamiento o necesidades del usuario.</li>
          <li>Recoger retroalimentación sobre los contenidos, ejercicios y experiencia general.</li>
          <li>
            Realizar análisis estadísticos, reportes de desempeño y evaluaciones agregadas (sin identificación
            individual) para mejorar la calidad de los servicios ofrecidos.
          </li>
        </ul>

        <h4>3.3. Finalidades comerciales y de comunicación</h4>
        <ul>
          <li>
            Enviar boletines, contenidos recomendados, actualizaciones de producto, recordatorios y notificaciones de
            interés relacionadas con inteligencia artificial, innovación y educación.
          </li>
          <li>
            Ofrecer nuevos productos, beneficios o descuentos a los usuarios, siempre con la opción de cancelar la
            suscripción a dichas comunicaciones.
          </li>
        </ul>

        <h4>3.4. Finalidades legales y de cumplimiento</h4>
        <ul>
          <li>
            Cumplir con obligaciones legales en materia tributaria, contable, de protección al consumidor y normativa
            vigente.
          </li>
          <li>Atender requerimientos de autoridades judiciales o administrativas debidamente facultadas.</li>
        </ul>
        <p>
          En todos los casos, el tratamiento se limitará a los fines previamente descritos. En caso de nuevas
          finalidades, se informará oportunamente al usuario para obtener su consentimiento explícito cuando sea
          necesario.
        </p>

        <h3>4. Legitimación del tratamiento</h3>
        <p>
          El tratamiento de datos personales por parte de LIDER IA se fundamenta en una o más de las siguientes bases
          legales, conforme a la normativa colombiana y, cuando aplique, al Reglamento General de Protección de Datos
          (GDPR) de la Unión Europea:
        </p>

        <h4>4.1. Consentimiento del titular</h4>
        <p>
          El consentimiento libre, previo, informado y expreso otorgado por el usuario al momento de registrarse o
          aceptar esta política. Dicho consentimiento puede ser revocado en cualquier momento, sin efecto retroactivo,
          mediante solicitud escrita.
        </p>

        <h4>4.2. Ejecución de un contrato</h4>
        <p>
          El tratamiento es necesario para la ejecución de un contrato en el que el usuario es parte (por ejemplo, una
          suscripción, certificación, acceso a contenidos o contratación de acompañamiento).
        </p>

        <h4>4.3. Cumplimiento de obligaciones legales</h4>
        <p>
          LIDER IA puede tratar datos cuando sea necesario para cumplir con normativas fiscales, contables,
          administrativas o requerimientos legales de autoridades competentes.
        </p>

        <h4>4.4. Interés legítimo del responsable</h4>
        <p>
          Cuando el tratamiento sea necesario para mejorar la calidad del servicio, prevenir fraudes, realizar análisis
          internos o desarrollar nuevas funcionalidades, siempre que no prevalezcan los derechos fundamentales del usuario.
        </p>
        <p>
          LIDER IA garantiza que toda actividad de tratamiento se realizará conforme a principios de legalidad,
          proporcionalidad, necesidad, finalidad, transparencia y seguridad.
        </p>

        <h3>5. Transferencias y tratamiento internacional</h3>
        <p>
          LIDER IA podrá almacenar, procesar o transferir datos personales fuera del territorio colombiano, incluyendo
          países como Estados Unidos o miembros de la Unión Europea, siempre que se cumplan los principios y garantías
          establecidos por la legislación nacional e internacional aplicable.
        </p>

        <h4>5.1. Localización de servidores y herramientas</h4>
        <p>
          La plataforma LIDER IA puede utilizar servicios de almacenamiento en la nube, herramientas de gestión educativa
          y sistemas de comunicación que se encuentran alojados en servidores ubicados fuera de Colombia. Estas
          infraestructuras tecnológicas están respaldadas por proveedores que implementan estándares internacionales de
          seguridad y privacidad.
        </p>

        <h4>5.2. Garantías para transferencias internacionales</h4>
        <p>
          Cuando los datos sean transferidos o tratados en países que no cuenten con un nivel de protección equivalente
          al colombiano o al europeo, LIDER IA garantizará:
        </p>
        <ul>
          <li>El uso de cláusulas contractuales tipo aprobadas por autoridades de protección de datos.</li>
          <li>
            La implementación de medidas técnicas y organizativas adicionales que aseguren la confidencialidad e
            integridad de la información.
          </li>
          <li>La limitación del tratamiento exclusivamente a las finalidades autorizadas por esta política.</li>
        </ul>
        <p>
          En ningún caso se transferirán datos a terceros sin contar con un marco legal adecuado o sin el consentimiento
          expreso del usuario cuando así lo exija la ley.
        </p>

        <h3>6. Uso de cookies y tecnologías similares</h3>
        <p>
          LIDER IA utiliza cookies y tecnologías similares para mejorar la experiencia de navegación, analizar el uso de
          la plataforma y personalizar el contenido mostrado a los usuarios.
        </p>

        <h4>6.1. ¿Qué son las cookies?</h4>
        <p>
          Las cookies son pequeños archivos de texto que se almacenan en el navegador del usuario cuando visita un sitio
          web. Sirven para recordar preferencias, facilitar el inicio de sesión, medir el rendimiento del sitio y ofrecer
          funcionalidades específicas.
        </p>

        <h4>6.2. Tipos de cookies utilizadas</h4>
        <p>LIDER IA puede emplear los siguientes tipos de cookies:</p>
        <ul>
          <li>
            <strong>Cookies técnicas o necesarias:</strong> habilitan funciones básicas como el inicio de sesión,
            navegación segura y carga de contenidos. Su desactivación podría impedir el uso correcto de la plataforma.
          </li>
          <li>
            <strong>Cookies de análisis:</strong> recopilan datos anónimos sobre el comportamiento del usuario para
            mejorar el contenido, la navegación y la usabilidad general del sitio.
          </li>
          <li>
            <strong>Cookies de personalización:</strong> adaptan el contenido mostrado al perfil del usuario, su progreso
            o intereses.
          </li>
          <li>
            <strong>Cookies de terceros:</strong> en caso de integrar herramientas externas (como pasarelas de pago o
            servicios de analítica), podrían generarse cookies adicionales, bajo responsabilidad de dichos proveedores.
          </li>
        </ul>

        <h4>6.3. Control y desactivación</h4>
        <p>
          El usuario puede configurar su navegador para aceptar, rechazar o eliminar cookies, así como recibir alertas
          cuando un sitio intente instalarlas. La desactivación de algunas cookies podría afectar el funcionamiento de
          ciertas partes de la plataforma.
        </p>
        <p>
          Al continuar navegando en la plataforma sin modificar la configuración del navegador, el usuario acepta el uso
          de cookies conforme a esta política.
        </p>

        <h3>7. Terceros y herramientas integradas</h3>
        <p>
          LIDER IA utiliza herramientas tecnológicas de terceros como parte de su infraestructura para automatizar
          procesos, generar contenidos personalizados y mejorar la experiencia del usuario. Estas herramientas se integran
          de forma controlada en los flujos internos de la plataforma.
        </p>

        <h4>7.1. Herramientas utilizadas y naturaleza del tratamiento</h4>
        <p>
          Entre los servicios de terceros que pueden recibir o procesar datos personales de usuarios bajo la gestión de
          LIDER IA se encuentran:
        </p>
        <ul>
          <li>
            <strong>OpenAI:</strong> para generar textos o sugerencias automáticas dentro de cápsulas, simuladores o
            acompañamientos personalizados.
          </li>
          <li>
            <strong>Make y n8n:</strong> para automatizar flujos operativos, como el envío de correos, generación de
            certificados o sincronización con bases de datos.
          </li>
          <li>
            <strong>Otras plataformas auxiliares:</strong> para soporte, notificaciones, formularios y monitoreo.
          </li>
        </ul>
        <p>
          Estas herramientas actúan como encargadas del tratamiento, y su uso se encuentra limitado a los fines
          autorizados por esta política, sin que tengan autonomía sobre los datos ni capacidad para utilizarlos con
          propósitos propios.
        </p>

        <h4>7.2. Controles aplicados</h4>
        <p>LIDER IA garantiza que:</p>
        <ul>
          <li>Solo se envían los datos estrictamente necesarios para cada flujo.</li>
          <li>La información se procesa bajo conexiones seguras y con autenticación.</li>
          <li>Los proveedores utilizados cumplen estándares internacionales de privacidad y seguridad.</li>
          <li>Se revisa periódicamente el uso y la configuración de estas integraciones.</li>
        </ul>

        <h4>7.3. Responsabilidad frente al tratamiento externo</h4>
        <p>
          LIDER IA asume la responsabilidad del tratamiento que se haga dentro de estos entornos, en la medida en que
          sean utilizados por su equipo directamente para brindar el servicio contratado. No obstante, si el usuario
          decide conectar de manera independiente sus propias cuentas a servicios externos, dicho tratamiento será ajeno
          a LIDER IA y bajo su propia responsabilidad.
        </p>

        <h3>8. Seguridad de la información</h3>
        <p>
          LIDER IA implementa medidas técnicas, administrativas y organizativas adecuadas para proteger los datos personales
          de sus usuarios frente a accesos no autorizados, pérdidas, alteraciones, uso indebido o divulgación no
          consentida.
        </p>

        <h4>8.1. Medidas implementadas</h4>
        <p>Entre las principales medidas adoptadas se incluyen:</p>
        <ul>
          <li>Acceso restringido a la información únicamente por personal autorizado.</li>
          <li>Autenticación y control de sesiones en la plataforma.</li>
          <li>Encriptación de comunicaciones mediante protocolo HTTPS.</li>
          <li>Integraciones con herramientas certificadas y monitoreadas (p. ej. Make, OpenAI, n8n).</li>
          <li>Almacenamiento en servicios en la nube con cifrado de datos y alta disponibilidad.</li>
          <li>Auditorías y revisiones periódicas de los flujos automatizados y sus permisos.</li>
        </ul>

        <h4>8.2. Gestión de incidentes</h4>
        <p>
          En caso de detectar una vulneración de seguridad que pueda comprometer los datos personales de los usuarios,
          LIDER IA aplicará un plan de respuesta que incluye:
        </p>
        <ul>
          <li>Evaluación técnica inmediata del alcance del incidente.</li>
          <li>Comunicación oportuna a los usuarios afectados, indicando riesgos y recomendaciones.</li>
          <li>Reporte a las autoridades competentes si así lo exige la ley.</li>
          <li>Acciones correctivas y preventivas para evitar recurrencias.</li>
        </ul>

        <h4>8.3. Limitaciones inherentes</h4>
        <p>
          Aunque LIDER IA aplica altos estándares de protección, el usuario reconoce que ningún sistema es infalible y
          que existen riesgos inherentes al uso de servicios digitales. Por tanto, también es responsabilidad del usuario:
        </p>
        <ul>
          <li>Mantener en secreto su contraseña y datos de acceso.</li>
          <li>No compartir sus credenciales con terceros.</li>
          <li>Informar de inmediato ante cualquier uso indebido o sospechoso.</li>
        </ul>

        <h3>9. Conservación y retención de datos</h3>
        <p>
          LIDER IA conservará los datos personales de los usuarios únicamente durante el tiempo necesario para cumplir
          con las finalidades para las cuales fueron recolectados, o mientras se mantenga vigente la relación contractual,
          comercial o académica con el usuario.
        </p>

        <h4>9.1. Tiempos de conservación</h4>
        <p>Los criterios aplicados para definir los periodos de retención son:</p>
        <ul>
          <li>
            <strong>Datos de acceso y uso de la plataforma:</strong> conservados mientras el usuario tenga una cuenta
            activa y hasta por 2 años después de la última actividad registrada, salvo solicitud de eliminación
            anticipada.
          </li>
          <li>
            <strong>Información asociada a certificados, evaluaciones y logros:</strong> podrá conservarse por un plazo de
            hasta 5 años, con fines de validación posterior o verificación por terceros.
          </li>
          <li>
            <strong>Datos financieros y de facturación:</strong> retenidos por el tiempo exigido por la normativa
            contable y tributaria (mínimo 5 años).
          </li>
          <li>
            <strong>Logs técnicos y de actividad automatizada:</strong> retenidos por 12 meses para auditoría, soporte y
            análisis de seguridad.
          </li>
        </ul>

        <h4>9.2. Eliminación y anonimización</h4>
        <p>
          Una vez cumplido el periodo de retención, los datos serán eliminados de manera segura o anonimizados para que
          no puedan ser asociados a un usuario específico. En caso de que el usuario ejerza su derecho a la supresión,
          LIDER IA procederá según lo estipulado en el Punto 10 de esta política.
        </p>

        <h3>10. Derechos de los usuarios (ARCO/GDPR)</h3>
        <p>
          LIDER IA garantiza a todos los titulares de datos personales el pleno ejercicio de sus derechos, conforme a la
          Ley 1581 de 2012 de Colombia, el Decreto 1377 de 2013 y, en su caso, el Reglamento General de Protección de
          Datos (GDPR) de la Unión Europea.
        </p>

        <h4>10.1. Derechos reconocidos</h4>
        <p>El usuario podrá ejercer, en cualquier momento, los siguientes derechos sobre sus datos:</p>
        <ul>
          <li>
            <strong>Acceso:</strong> Conocer qué datos suyos están siendo tratados, la finalidad del tratamiento y su
            origen.
          </li>
          <li>
            <strong>Rectificación:</strong> Solicitar la corrección de datos inexactos, incompletos o desactualizados.
          </li>
          <li>
            <strong>Cancelación o supresión:</strong> Pedir la eliminación de sus datos cuando considere que no son
            necesarios o cuando haya retirado su consentimiento.
          </li>
          <li>
            <strong>Oposición:</strong> Negarse al tratamiento de sus datos por motivos legítimos o en relación con
            determinadas finalidades.
          </li>
          <li>
            <strong>Limitación del tratamiento:</strong> Solicitar que se suspenda temporalmente el tratamiento de sus
            datos en ciertos casos.
          </li>
          <li>
            <strong>Portabilidad:</strong> Recibir sus datos personales en un formato estructurado y legible para
            transferirlos a otro proveedor (aplicable según el GDPR).
          </li>
        </ul>

        <h4>10.2. Ejercicio de derechos</h4>
        <p>
          El usuario podrá ejercer estos derechos enviando una solicitud escrita al correo electrónico{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          , incluyendo:
        </p>
        <ul>
          <li>Nombre completo y número de identificación.</li>
          <li>Descripción clara del derecho que desea ejercer.</li>
          <li>Pruebas que soporten su solicitud (si aplica).</li>
          <li>Correo o medio para recibir la respuesta.</li>
        </ul>
        <p>
          LIDER IA atenderá la solicitud en un plazo máximo de 15 días hábiles, contados a partir de su recepción. Si la
          solicitud está incompleta, se solicitará la información faltante dentro de los 5 días hábiles siguientes.
        </p>

        <h3>11. Protección de menores</h3>
        <p>
          LIDER IA reconoce la importancia de proteger los datos personales de niñas, niños y adolescentes, y aplica
          medidas especiales cuando se identifica que los usuarios son menores de edad.
        </p>

        <h4>11.1. Acceso supervisado</h4>
        <p>
          La plataforma está dirigida principalmente a personas mayores de edad. Sin embargo, los menores de edad pueden
          hacer uso de los contenidos únicamente si:
        </p>
        <ul>
          <li>
            Están inscritos por una institución educativa, empresa o entidad que actúe como responsable del grupo.
          </li>
          <li>
            Cuentan con el consentimiento informado y supervisión activa de un adulto responsable (padre, madre, tutor o
            representante autorizado).
          </li>
        </ul>
        <p>
          El adulto responsable será quien debe aceptar los presentes Términos y Condiciones y la Política de Datos en
          nombre del menor, y responderá por el adecuado uso de la plataforma.
        </p>

        <h4>11.2. Tratamiento de datos de menores</h4>
        <p>Los datos personales de menores recolectados dentro de este contexto serán tratados bajo los siguientes principios:</p>
        <ul>
          <li>Finalidad exclusivamente educativa y formativa.</li>
          <li>Uso restringido a los servicios contratados.</li>
          <li>Prohibición de tratamiento para fines comerciales o de marketing.</li>
          <li>Derecho prioritario del menor a la protección de su integridad y privacidad.</li>
        </ul>
        <p>
          En caso de que LIDER IA tenga conocimiento de que se ha recolectado información de un menor sin el
          consentimiento de un adulto responsable, se procederá a eliminarla de inmediato.
        </p>

        <h3>12. Cambios en la política</h3>
        <p>
          LIDER IA se reserva el derecho de modificar, actualizar o complementar esta Política de Protección de Datos en
          cualquier momento, con el fin de ajustarla a cambios normativos, mejoras tecnológicas o transformaciones en los
          servicios ofrecidos.
        </p>

        <h4>12.1. Notificación de cambios</h4>
        <p>Las modificaciones serán informadas a través de los siguientes medios:</p>
        <ul>
          <li>
            Publicación de la nueva versión en la plataforma{' '}
            <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>
          </li>
          <li>Correo electrónico a los usuarios registrados (cuando los cambios sean sustanciales).</li>
        </ul>
        <p>
          La nueva versión entrará en vigor desde su publicación, salvo que se indique un plazo distinto. El uso
          continuado de la plataforma por parte del usuario después de realizada la modificación se considerará como
          aceptación expresa de los nuevos términos.
        </p>

        <h4>12.2. Derecho de oposición</h4>
        <p>
          Si el usuario no está de acuerdo con las modificaciones realizadas, podrá ejercer su derecho de supresión de
          datos o cancelar su cuenta, en cuyo caso se aplicarán los procedimientos establecidos en esta política.
        </p>

        <h3>13. Contacto y ejercicio de derechos</h3>
        <p>
          Para ejercer cualquiera de los derechos descritos en esta política, así como para presentar consultas, reclamos
          o solicitudes relacionados con el tratamiento de datos personales, el usuario puede comunicarse con el equipo de
          LIDER IA a través de los siguientes canales:
        </p>
        <ul>
          <li>
            <strong>Correo electrónico oficial:</strong>{' '}
            <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
              hello@soylideria.com
            </a>
          </li>
          <li>
            <strong>Plataforma web:</strong>{' '}
            <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>
          </li>
          <li>
            <strong>Razón social responsable:</strong> VIVETORI S.A.S. – NIT 901560075
          </li>
        </ul>
        <p>
          El equipo de soporte responderá en un plazo máximo de 15 días hábiles, conforme a lo establecido por la Ley
          1581 de 2012 y demás disposiciones aplicables.
        </p>
        <p>
          En caso de que el usuario considere que sus derechos han sido vulnerados, también podrá acudir ante la
          Superintendencia de Industria y Comercio (SIC) en Colombia, o la autoridad competente de protección de datos
          del país donde resida, si aplica el marco del GDPR.
        </p>
      </article>
    </div>
  );
}
